<template>
  <BCard>
    <h3 class="mb-2 text-10 font-medium">
      Data Produk
    </h3>
    <div
      v-for="product, idx in products"
      :key="idx"
    >
      <b class="block w-full text-[14px] text-[#333333] pb-[6px] border-b border-[#E2E2E2]">Produk {{ idx + 1 }}</b>
      <BRow class="pt-[12px] pb-[24px]">
        <BCol md="8">
          <BFormGroup
            label-cols-md="4"
            label-class="font-medium text-7 text-black"
          >
            <template #label>
              Nama Produk <span class="text-danger">*</span>
            </template>
            <div>
              <BFormInput
                :value="product.name"
                type="search"
                placeholder="Tambah produk"
                :disabled="isDisabled"
                @keydown="e => limitInput(e, product.name)"
                @input="name => handleInputProduct(name, idx)"
              />
              <small
                v-if="checkProductDuplication(product)"
                class="text-danger"
              >Nama produk sudah digunakan, silakan gunakan nama lain</small>
            </div>
          </BFormGroup>
        </BCol>
        <BCol md="8">
          <BFormGroup
            label-cols-md="4"
            label-class="font-medium text-7 text-black"
          >
            <template #label>
              Alasan Bounce <span class="text-danger">*</span>
            </template>
            <MultiSelector
              class="grow"
              :name="`bounce-${idx}`"
              mode="default"
              placeholder="Pilih atau cari alasan bounce"
              editable-options
              pushable-options
              :master-options-number="masterOptionsNumber"
              :disabled="isDisabled"
              :model-value="reasonModels[idx]"
              :model-search.sync="searchBounces[idx]"
              :options="filteredBounces[idx]"
              :loading="loadBounce"
              :loading-add-option="loadAddBounceOption[idx].load"
              :error="errors[idx].text"
              @update:options="options => { bounces = options }"
              @update:modelValue="bounce_reason => handleSelectOption(bounce_reason, idx)"
              @onAddOption="data => handleAddOption(data, idx)"
            />
          </BFormGroup>
        </BCol>
        <BCol
          md="8"
          class="flex justify-end"
        >
          <b-button
            v-if="products.length > 1"
            variant="outline-primary"
            class="!px-[10px] !py-[6px] h-[32px] !flex items-center gap-[10px] !text-[14px] font-semibold"
            @click="() => openRemoveProductPopup(product)"
          >
            <i class="k-trash text-2xl font-semibold" />
            Hapus Produk
          </b-button>
        </BCol>
      </BRow>
    </div>
    <b-button
      v-if="products.length < 10"
      variant="primary"
      class="!px-[14px] mt-[8px] h-[38px] !flex items-center gap-[10px] !text-[14px] font-semibold disabled:!bg-[#F8F8F8] disabled:!opacity-100 disabled:!border-[#E2E2E2] disabled:pointer-events-none disabled:!text-[#C2C2C2]"
      :disabled="disableAddProduct"
      @click="handleAddProduct"
    >
      <i class="k-add text-2xl" />
      Tambah Produk
    </b-button>
    <ConfirmPopup
      id="remove-product"
      title="Hapus Produk?"
      text="Produk yang kamu hapus tidak akan tampil di halaman Report Performa dan Aplikasi Mitra Komerce Talent!"
      confirm-text="Iya"
      cancel-text="Batal"
      @confirm="() => handleRemoveProduct(productToRemove)"
    />
  </BCard>
</template>

<script>
import { komtimAxiosIns } from '@/libs/axios'
import MultiSelector from '@/views/pages/komtim/invoices/invoice-admin/components/MultiSelector.vue'
import ConfirmPopup from '@/views/pages/komtim/training/training-program/ConfirmPopup.vue'

export default {
  components: { MultiSelector, ConfirmPopup },
  props: {
    products: {
      type: Array,
      default: () => [
        {
          id: 'product-0', name: '', price: 0, bounce_reason: [],
        },
      ],
    },
    errors: {
      type: Array,
      default: () => [
        { id: 'product-0', text: '' },
      ],
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      bounces: [],
      searchBounces: this.products.map(() => ''),
      loadBounce: false,
      productToRemove: null,
      loadAddBounceOption: this.products.map(product => ({ id: product.id, load: false })),
      error: {
        select: 'Minimal 1 alasan bounce harus diisi',
      },
      masterOptionsNumber: 6,
      defaultProducts: [],
    }
  },
  computed: {
    disableAddProduct() {
      return this.products.some(product => !product.name || product.bounce_reason.length === 0)
    },
    reasonModels() {
      return this.products.map(product => product.bounce_reason.map(reason => reason.id))
    },
    filteredBounces() {
      return this.products.map((_, idx) => {
        const query = this.searchBounces[idx]
        if (!query) return this.bounces
        return this.bounces.filter(bounce => bounce.text.toLowerCase().includes(query.toLowerCase()))
      })
    },
  },
  watch: {
    products: {
      handler(val, oldVal) {
        if (val && val.length !== oldVal.length) {
          this.searchBounces = this.products.map(() => '')
          this.loadAddBounceOption = this.products.map(product => ({ id: product.id, load: false }))
        }
      },
    },
    isDisabled(value) {
      if (value) {
        this.$emit('update:products', this.defaultProducts)
        this.$emit('update:errors', [{ id: 'product-0', text: '' }])
      }
    },
  },
  mounted() {
    this.getOptions()
  },
  methods: {
    handleSelectOption(reason, idx) {
      const { id } = this.products[idx]
      const transformedReason = reason.map(r => ({ id: r, name: this.bounces.find(bounce => bounce.value === r).text }))
      const newProducts = this.products.map((value, i) => (i === idx ? ({ ...value, bounce_reason: transformedReason }) : value))
      const newErrors = this.errors.map((value, i) => (i === idx ? ({ id, text: reason.length === 0 ? this.error.select : '' }) : value))
      this.$emit('update:products', newProducts)
      this.$emit('update:errors', newErrors)
    },
    async getOptions() {
      this.loadBounce = true
      try {
        const { data: { data } } = await komtimAxiosIns.get('/v1/bounce_reasons')
        this.bounces = data.map(({ id, name }) => ({ value: id, text: name }))
        if (this.products.length === 1 && !this.products[0].name) {
          const newProducts = this.products.map(product => ({ ...product, bounce_reason: data.slice(0, this.masterOptionsNumber) }))
          this.defaultProducts = newProducts
          this.$emit('update:products', newProducts)
        }
      } catch (error) {
        this.$toast_error({ message: 'Gagal memperoleh data alasan bounce' })
      } finally {
        this.loadBounce = false
      }
    },
    async handleAddOption({ text, toLast }, idx) {
      if (this.loadAddBounceOption[idx].load) return
      if (this.bounces.some(bounce => bounce.text.toLowerCase() === text.toLowerCase())) {
        this.$toast_error({ message: 'Alasan bounce yang ingin ditambahkan sudah tersedia' })
        return
      }
      this.loadAddBounceOption[idx].load = true
      try {
        const { data: { data: { id, name } } } = await komtimAxiosIns.post('/v1/bounce_reasons', { name: text })
        this.bounces.push({ value: id, text: name })
        toLast()
      } catch (error) {
        this.$toast_error({ message: 'Gagal menambahkan alasan bounce' })
      } finally {
        this.loadAddBounceOption[idx].load = false
      }
    },
    handleAddProduct() {
      if (this.disableAddProduct) return
      const id = `product-${this.products.length}`
      const newProducts = [...this.products, {
        id, name: '', price: 0, bounce_reason: this.bounces.slice(0, this.masterOptionsNumber).map(({ value, text }) => ({ id: value, name: text })),
      }]
      const newErrors = [...this.errors, { id, text: '' }]
      this.$emit('update:products', newProducts)
      this.$emit('update:errors', newErrors)
    },
    openRemoveProductPopup(val) {
      this.productToRemove = val
      this.$bvModal.show('remove-product')
    },
    handleRemoveProduct(product) {
      const newProducts = this.products.filter(p => p.id !== product.id)
      const newErrors = this.errors.filter(error => error.id !== product.id)
      this.$emit('update:products', newProducts)
      this.$emit('update:errors', newErrors)
    },
    handleInputProduct(name, idx) {
      const { id, bounce_reason } = this.products[idx]
      const newProducts = this.products.map((value, i) => (i === idx ? ({ ...value, name }) : value))
      const newErrors = this.errors.map((value, i) => (i === idx ? ({ id, text: name && bounce_reason.length === 0 ? this.error.select : '' }) : value))
      this.$emit('update:products', newProducts)
      this.$emit('update:errors', newErrors)
    },
    limitInput(e, currentValue) {
      if (e.ctrlKey || e.metaKey) return
      if (currentValue.length >= 50 && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
        e.preventDefault()
      }
    },
    checkProductDuplication(product) {
      const seen = new Set()
      const duplicates = new Set()
      const filledProducts = this.products.filter(p => p.name)
      filledProducts.forEach(({ name }) => (seen.has(name) ? duplicates.add(name) : seen.add(name)))
      return duplicates.has(product.name)
    },
  },
}
</script>
